/**
 * Keeps chat identifiers out of `/redirect` page addresses.
 *
 * A `/redirect` link built on a chat page used to carry `thread_id` and a
 * `clickout_url` of `/chats/<hash>/<threadId>`. Anyone who saw that address
 * could open the shopper's chat (`GET /api/chat/brain/sessions/:threadId`
 * serves it to any caller) and walk its product list. During the 2026-09
 * redirect bot farm (docs/postmortems/2026-09-21-redirect-bot-farm-affiliate-clicks.md),
 * ~83% of bot `/redirect` loads came from a thread a real shopper had clicked
 * out of 5–11 minutes earlier, most of them for products that shopper never
 * clicked.
 *
 * Instead, the two values go into localStorage (shared with the new tab the
 * link opens in; sessionStorage is not, because the links use `noopener`) and
 * the link carries only `rcc=<key>`. The `/redirect` page reads them back and
 * forwards them to `/api/redirect` exactly as before, so `link_clicks` rows
 * are unchanged. Whenever the hand-off can't work — no storage, a framed page
 * whose storage is partitioned, a link to another origin — the values stay on
 * the URL as they always have. Attribution is never traded for this.
 */

export const REDIRECT_CHAT_CONTEXT_PARAM = 'rcc'

const STORAGE_PREFIX = 'dupe:rcc:'
const SALT_KEY = 'dupe:rcc-salt'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

type StoredContext = { t?: string; c?: string; m?: string; at: number }

export type RedirectChatContext = {
  threadId?: string
  clickoutUrl?: string
  /** The fragrance answer (`mid`): it names a message in the chat, so it
   *  travels with the thread id, never on the link. */
  messageId?: string
}

let pruned = false

const isChatPath = (path: string | null | undefined): boolean =>
  !!path && path.startsWith('/chats/')

const getStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null
    // A framed page (e.g. the extension side panel) gets partitioned storage
    // that the top-level tab the link opens in cannot read.
    if (window.top !== window.self) return null
    return window.localStorage
  } catch {
    return null
  }
}

const getSalt = (storage: Storage): string | null => {
  let salt = storage.getItem(SALT_KEY)
  if (!salt) {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    salt = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    storage.setItem(SALT_KEY, salt)
    if (storage.getItem(SALT_KEY) !== salt) return null
  }
  return salt
}

// FNV-1a over the salted input, twice with different offsets for 64 bits.
// Deterministic so a card's href is stable across re-renders. The key can't be
// traced back to a thread only because of the per-browser salt, which never
// leaves this browser; FNV-1a itself is not a one-way hash.
const keyFor = (salt: string, input: string): string => {
  const hash = (seed: number) => {
    let h = seed >>> 0
    const s = `${salt}|${input}`
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i)
      h = Math.imul(h, 0x01000193) >>> 0
    }
    return h.toString(36)
  }
  return `${hash(0x811c9dc5)}${hash(0x2166136)}`
}

/** The stored entry, or null when it is missing, unreadable or expired. */
const readFresh = (
  storage: Storage,
  storageKey: string
): StoredContext | null => {
  const raw = storage.getItem(storageKey)
  if (!raw) return null
  const value = JSON.parse(raw) as StoredContext
  return Date.now() - value.at < TTL_MS ? value : null
}

const pruneExpired = (storage: Storage) => {
  if (pruned) return
  pruned = true
  const now = Date.now()
  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i)
    if (!key?.startsWith(STORAGE_PREFIX)) continue
    try {
      const value = JSON.parse(storage.getItem(key) || '') as StoredContext
      if (!(now - value.at < TTL_MS)) storage.removeItem(key)
    } catch {
      storage.removeItem(key)
    }
  }
}

/**
 * Moves `thread_id` and a chat `clickout_url` off a `/redirect` URL into
 * localStorage, replacing them with `rcc=<key>`. Leaves the URL untouched
 * when there is nothing chat-identifying on it or the hand-off can't work.
 */
export const moveChatContextOffRedirectUrl = (urlObj: URL): void => {
  try {
    if (typeof window === 'undefined') return
    if (!urlObj.pathname.startsWith('/redirect')) return
    if (urlObj.origin !== window.location.origin) return

    // A link can pass through here twice (convertToRedirectUrl, then
    // enrichRedirectUrl adding clickout_url): merge into the earlier entry.
    const earlier = readRedirectChatContext(
      urlObj.searchParams.get(REDIRECT_CHAT_CONTEXT_PARAM)
    )
    const threadId =
      urlObj.searchParams.get('thread_id') || earlier?.threadId || undefined
    const clickoutUrl =
      urlObj.searchParams.get('clickout_url') ||
      earlier?.clickoutUrl ||
      undefined
    const messageId =
      urlObj.searchParams.get('mid') || earlier?.messageId || undefined
    if (!threadId && !isChatPath(clickoutUrl)) return

    const storage = getStorage()
    if (!storage) return
    const salt = getSalt(storage)
    if (!salt) return

    // ⛔ The answer is in the key: every answer in a chat gets its own entry,
    // or the first one's id would ride every later buy link.
    const key = keyFor(
      salt,
      `${threadId ?? ''}|${clickoutUrl ?? ''}${messageId ? `|${messageId}` : ''}`
    )
    const storageKey = STORAGE_PREFIX + key
    // An expired entry is rewritten, not reused: the read side rejects it, so
    // a shopper back on a weeks-old chat still hands over a live thread id.
    if (!readFresh(storage, storageKey)) {
      pruneExpired(storage)
      const value: StoredContext = {
        t: threadId,
        c: clickoutUrl,
        m: messageId,
        at: Date.now(),
      }
      storage.setItem(storageKey, JSON.stringify(value))
      if (!storage.getItem(storageKey)) return
    }

    urlObj.searchParams.delete('thread_id')
    urlObj.searchParams.delete('clickout_url')
    urlObj.searchParams.delete('mid')
    urlObj.searchParams.set(REDIRECT_CHAT_CONTEXT_PARAM, key)
  } catch {
    // Any failure leaves the URL as it was.
  }
}

/** Reads back what `moveChatContextOffRedirectUrl` stored under `key`. */
export const readRedirectChatContext = (
  key: string | null | undefined
): RedirectChatContext | null => {
  try {
    if (!key) return null
    const storage = getStorage()
    if (!storage) return null
    const value = readFresh(storage, STORAGE_PREFIX + key)
    if (!value) return null
    return { threadId: value.t, clickoutUrl: value.c, messageId: value.m }
  } catch {
    return null
  }
}

/**
 * The `/redirect` page's own path + search as `/api/redirect` has always
 * received it in `clickout_url`: with `rcc` swapped back for the `thread_id`
 * and chat `clickout_url` it stands for, so server-side parsing is unchanged.
 */
export const redirectPageSourceUrl = (): string => {
  const { pathname, search } = window.location
  const params = new URLSearchParams(search)
  const context = readRedirectChatContext(
    params.get(REDIRECT_CHAT_CONTEXT_PARAM)
  )
  if (!context) return pathname + search
  params.delete(REDIRECT_CHAT_CONTEXT_PARAM)
  if (context.threadId && !params.has('thread_id')) {
    params.set('thread_id', context.threadId)
  }
  if (context.clickoutUrl && !params.has('clickout_url')) {
    params.set('clickout_url', context.clickoutUrl)
  }
  const rebuilt = params.toString()
  return rebuilt ? `${pathname}?${rebuilt}` : pathname
}

/** `query` with `thread_id` and `mid` restored from `rcc`, each when it isn't already set. */
export const withRestoredChatContext = <T extends Record<string, unknown>>(
  query: T | null | undefined
): T | null | undefined => {
  const key = query?.[REDIRECT_CHAT_CONTEXT_PARAM]
  if (!query || typeof key !== 'string' || (query.thread_id && query.mid))
    return query
  const context = readRedirectChatContext(key)
  const threadId = query.thread_id ? null : context?.threadId
  const messageId = query.mid ? null : context?.messageId
  if (!threadId && !messageId) return query
  return {
    ...query,
    ...(threadId ? { thread_id: threadId } : {}),
    ...(messageId ? { mid: messageId } : {}),
  }
}
